import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import type { ModifierGroupFormItem, ModifierGroupModifierFormItem } from "./ModifierGroupForm"
import { convertDollarsToCents } from "../../../../utils/helpers"
import { CategoryModifierGroup, CategoryModifierGroupOverride } from "../../../../graphql/types"
import { getModifierById } from "../modifiers/selectors"
import {
    ModifierGroupAddRequestedPayload,
    ModifierGroupModifier,
    ModifierGroupUpdateRequestedPayload,
} from "../../../../events/modifier/types"
import * as uuid from "uuid"

const handlePricing = (isSet, price, defaultReturnValue: number | null = 0, multiply: Maybe<number> = 1) => {
    if (!isSet || price == null) {
        return defaultReturnValue
    }

    if (isSet && price != null && multiply) {
        return Number.parseFloat((price * multiply).toFixed(2))
    }

    return defaultReturnValue
}

export const composeModifierGroupAddedRequestData = (
    formItem: ModifierGroupFormItem,
    locationId: Maybe<string>
): ModifierGroupAddRequestedPayload => {
    const modifierGroupId = formItem.modifierGroupId || uuid.v7()
    const groupPrice = handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedForPrice, null)
    return {
        groupId: modifierGroupId,
        locationId: locationId ?? undefined,
        name: formItem.name,
        displayName: formItem.displayName,
        isActive: formItem.isActive == null ? true : formItem.isActive,
        isActivePOS: formItem.isActivePOS == null ? true : formItem.isActivePOS,
        isActiveMIB: formItem.isActiveMIB == null ? true : formItem.isActiveMIB,
        isActiveKiosk: formItem.isActiveKiosk == null ? true : formItem.isActiveKiosk,
        displayType: formItem.display,
        isExclusion: formItem.exclusion,
        min: handlePricing(formItem.isMinimumSelection, formItem.minimumSelection, null),
        max: handlePricing(formItem.isMaximumSelection, formItem.maximumSelection, null),
        free: handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedCount, null),
        groupPrice: groupPrice != null ? convertDollarsToCents(groupPrice) : null,
        addedItemsIds: formItem.addedToItems ? formItem.addedToItems.map(item => item.id) : [],
        modifiers: formItem.items.map((modifier: ModifierGroupModifierFormItem) => {
            return {
                groupItemId: modifier.groupItemId || uuid.v7(),
                categoryId: modifier.modifierId,
                price: modifier.price ? convertDollarsToCents(modifier.price) : 0,
                supplement: modifier.groupExtra ? convertDollarsToCents(modifier.groupExtra) : 0,
            }
        }),
    }
}

export const composeModifierGroupUpdateRequestData = (
    formItem: ModifierGroupFormItem,
    prevValues: ModifierGroupFormItem,
    locationId: Maybe<string>
): ModifierGroupUpdateRequestedPayload => {
    const modifierGroupId = formItem.modifierGroupId || uuid.v7()
    const groupPrice = handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedForPrice, null)
    const addedItemIdsChanged = formItem.addedToItems !== prevValues.addedToItems
    return {
        groupId: modifierGroupId,
        locationId: locationId ?? undefined,
        name: formItem.name,
        displayName: formItem.displayName,
        isActive: formItem.isActive == null ? true : formItem.isActive,
        isActivePOS: formItem.isActivePOS == null ? true : formItem.isActivePOS,
        isActiveMIB: formItem.isActiveMIB == null ? true : formItem.isActiveMIB,
        isActiveKiosk: formItem.isActiveKiosk == null ? true : formItem.isActiveKiosk,
        displayType: formItem.display,
        isExclusion: formItem.exclusion,
        min: handlePricing(formItem.isMinimumSelection, formItem.minimumSelection, null),
        max: handlePricing(formItem.isMaximumSelection, formItem.maximumSelection, null),
        free: handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedCount, null),
        groupPrice: groupPrice != null ? convertDollarsToCents(groupPrice) : null,
        addedItemsIds:
            formItem.addedToItems && addedItemIdsChanged ? formItem.addedToItems.map(item => item.id) : undefined,
        modifiers: formItem.items.map((modifier: ModifierGroupModifierFormItem) => {
            return {
                groupItemId: modifier.groupItemId || uuid.v7(),
                categoryId: modifier.modifierId,
                price: modifier.price ? convertDollarsToCents(modifier.price) : 0,
                supplement: modifier.groupExtra ? convertDollarsToCents(modifier.groupExtra) : 0,
            }
        }),
    }
}

export const composeModifierGroupFromAddPayload = (
    payload: ModifierGroupAddRequestedPayload,
    formValues: ModifierGroupFormItem,
    deps: EpicDependencies
): CategoryModifierGroup => {
    return {
        id: payload.groupId,
        name: payload.name,
        displayName: payload.displayName,
        price: (payload.modifiers || []).map(m => m.price ?? 0),
        displayType: payload.displayType,
        isActive: payload.isActive,
        isActivePOS: payload.isActivePOS,
        isActiveMIB: payload.isActiveMIB,
        isActiveKiosk: payload.isActiveKiosk,
        isExclusion: payload.isExclusion,
        min: payload.min,
        max: payload.max,
        free: payload.free,
        groupPrice: payload.groupPrice,
        modifiers: (payload.modifiers || []).map((m: ModifierGroupModifier) => {
            const modifier = getModifierById(deps.getState(), m.categoryId)
            return {
                id: m.groupItemId,
                categoryId: m.categoryId,
                name: modifier ? modifier.name : "Unnamed",
                price: m.price,
                supplement: m.supplement,
            }
        }),
        addedToItems: formValues.addedToItems,
    }
}

export const composeModifierGroupFromUpdatePayload = (
    payload: ModifierGroupUpdateRequestedPayload,
    formValues: ModifierGroupFormItem,
    deps: EpicDependencies
): CategoryModifierGroup => {
    return {
        id: payload.groupId,
        name: payload.name || formValues.name,
        displayName: payload.displayName,
        price: (payload.modifiers || []).map(m => m.price ?? 0),
        displayType: payload.displayType || formValues.display,
        isActive: payload.isActive ?? true,
        isActivePOS: payload.isActivePOS,
        isActiveMIB: payload.isActiveMIB,
        isActiveKiosk: payload.isActiveKiosk,
        isExclusion: payload.isExclusion ?? false,
        min: payload.min,
        max: payload.max,
        free: payload.free,
        groupPrice: payload.groupPrice,
        modifiers: (payload.modifiers || []).map((m: ModifierGroupModifier) => {
            const modifier = getModifierById(deps.getState(), m.categoryId)
            return {
                id: m.groupItemId,
                categoryId: m.categoryId,
                name: modifier ? modifier.name : "Unnamed",
                price: m.price,
                supplement: m.supplement,
            }
        }),
        addedToItems: formValues.addedToItems,
    }
}

export const composeModifierGroupOverrideFromUpdatePayload = (
    payload: ModifierGroupUpdateRequestedPayload,
    originGroup: Maybe<CategoryModifierGroup>,
    selectedLocationId: string
): CategoryModifierGroupOverride => {
    return {
        locationId: selectedLocationId,
        data: {
            id: payload.groupId,
            isActive: payload.isActive,
            min: payload.min,
            max: payload.max,
            free: payload.free,
            groupPrice: payload.groupPrice,
            modifiers: payload.modifiers.map(m => {
                const originModifier = originGroup
                    ? originGroup.modifiers.find(originM => originM.id === m.groupItemId)
                    : null

                if (!originModifier) {
                    return {
                        id: m.groupItemId,
                        price: m.price,
                        supplement: m.supplement,
                    }
                }

                return {
                    id: m.groupItemId,
                    price: m.price === originModifier.price ? null : m.price,
                    supplement: m.supplement === originModifier.supplement ? null : m.supplement,
                }
            }),
        },
    }
}
